import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { logger } from '@/lib/logger';

/**
 * Determine if the current user has edit rights for a given floor plan.
 * With Clerk auth, all organization members have edit access to their org's floor plans.
 */
export const useFloorPlanEditPermission = (floorPlanId?: string) => {
  const { isSignedIn } = useClerkAuth();

  return useQuery<{ canEdit: boolean }, Error>({
    queryKey: ['floorPlanCanEdit', floorPlanId, isSignedIn],
    queryFn: async () => {
      if (!floorPlanId || !isSignedIn) return { canEdit: false };

      try {
        // Get floor plan with its project to check if it belongs to user's org
        const supabase = await getAuthenticatedSupabaseClient();
        const { data: floorPlan, error: fpError } = await supabase
          .from('floor_plans')
          .select('project_id, projects!inner(clerk_org_id)')
          .eq('id', floorPlanId)
          .maybeSingle();

        if (fpError) {
          logger.log('Error fetching floor plan:', fpError);
          return { canEdit: false };
        }

        if (!floorPlan) {
          return { canEdit: false };
        }

        // With Clerk, if user is signed in and the project exists, they have access
        // The RLS policies will handle the actual access control
        return { canEdit: true };
      } catch (error) {
        logger.error('Error checking floor plan permissions:', error);
        return { canEdit: false };
      }
    },
    enabled: !!floorPlanId && isSignedIn,
    staleTime: 1000 * 60, // 1 min cache
  }) as any;
};
